import { useState } from 'react'
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { Box, Stack, Typography, darken } from '@mui/material'
import { theme } from '@ketch-com/deck'
import dayjs from 'dayjs'

export type InvokedFulfilledData = {
  date: string
  invoked: number
  fulfilled: number
}

type Props = {
  data: InvokedFulfilledData[]
  isDisabled?: boolean
}

const COLORS = {
  invoked: theme.palette.Infographics.VioletA.Secondary,
  fulfilled: theme.palette.Success.Primary,
}

export const InvokedFulfilledChart = ({ data }: Props) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const yAxisMax = Math.max(1, ...data.map(d => Math.max(d.invoked, d.fulfilled)))

  return (
    <Box position="relative">
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={data}
          barSize={10}
          style={{ opacity: 1 }}
          onMouseMove={state => {
            if (state.isTooltipActive && state.activeTooltipIndex !== undefined) {
              setActiveIndex(state.activeTooltipIndex)
            } else {
              setActiveIndex(null)
            }
          }}
          onMouseLeave={() => setActiveIndex(null)}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <XAxis
            dataKey="date"
            tick={{
              fontSize: theme.typography.smallBody.fontSize,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.smallBody.fontWeight,
            }}
            stroke={theme.palette.Black.o48}
            tickLine={false}
            tickFormatter={dateStr => dayjs(dateStr).format('MM/DD')}
          />
          <YAxis
            domain={[0, yAxisMax]}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            tickFormatter={num => {
              if (num >= 1e6) return `${(num / 1e6).toFixed(1)}m`
              if (num >= 1e3) return `${(num / 1e3).toFixed(1)}k`
              return num
            }}
            tick={{
              fontSize: theme.typography.smallBody.fontSize,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.smallBody.fontWeight,
            }}
          />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ active, payload, label }) => {
              if (active && payload?.length) {
                const formattedLabel = dayjs(label).format('dddd, M/D/YYYY')
                return (
                  <Stack
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      background: theme.palette.Black.o100,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Typography variant="smallLabel" color={theme.palette.White.o100}>
                      {formattedLabel}
                    </Typography>
                    <Stack spacing={0.25}>
                      {payload
                        .filter(p => p.name === 'Invoked' || p.name === 'Fulfilled')
                        .map(p => (
                          <Typography key={p.dataKey} variant="smallBody" color={theme.palette.White.o100}>
                            {p.name}: <Typography variant="smallLabel">{p.value}</Typography>
                          </Typography>
                        ))}
                    </Stack>
                  </Stack>
                )
              }
              return null
            }}
          />
          <Bar dataKey="invoked" name="Invoked" fill={COLORS.invoked}>
            {data.map((item, i) => (
              <Cell key={item.date} fill={activeIndex === i ? darken(COLORS.invoked, 0.2) : COLORS.invoked} />
            ))}
          </Bar>
          <Bar dataKey="fulfilled" name="Fulfilled" fill={COLORS.fulfilled}>
            {data.map((item, i) => (
              <Cell key={item.date} fill={activeIndex === i ? darken(COLORS.fulfilled, 0.2) : COLORS.fulfilled} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
